import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Badge, Button, Card, Field, Icon, IconBtn, Input } from "../../design-system";
import { FORMACION_ACCENTS, FORMACION_CATEGORIES, fmtMinutes } from "./data";
import { CourseThumb } from "./atoms";

// ---------- helpers ----------
function humanSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
function extOf(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "file";
}
function durToMin(d) {
  const m = /^(\d{1,2}):(\d{2})$/.exec((d || "").trim());
  if (!m) return 0;
  return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
}
let _uid = 0;
const uid = (p) => `${p}-${Date.now().toString(36)}-${_uid++}`;

const ACCENT_KEYS = ["brand", "info", "pending", "warning", "danger", "slate"];
const CONTENT_CATS = FORMACION_CATEGORIES.filter((c) => c.id !== "todos" && c.id !== "obligatorio");

const linkBtn = { border: "none", background: "transparent", color: "var(--kr-fg-link)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "0 4px" };
const addRowBtn = { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, height: 32, padding: "0 12px", border: "1px dashed var(--kr-border-strong)", background: "var(--kr-bg-surface)", color: "var(--kr-fg-strong)", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" };
const taStyle = { width: "100%", border: "1px solid var(--kr-border)", borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.5 };

// ---------- select estilizado ----------
function FmSelect({ value, onChange, options, placeholder, style = {} }) {
  return (
    <div style={{ position: "relative", ...style }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: "none", WebkitAppearance: "none", width: "100%", height: 36,
          border: "1px solid var(--kr-border)", borderRadius: 6, background: "var(--kr-bg-surface)",
          padding: "0 32px 0 12px", fontSize: 13, color: value ? "var(--kr-fg-strong)" : "var(--kr-fg-subtle)",
          fontFamily: "var(--kr-font-sans)", cursor: "pointer", outline: "none",
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <Icon name="ChevronDown" size={14} style={{ color: "var(--kr-fg-muted)", position: "absolute", right: 10, top: 11, pointerEvents: "none" }} />
    </div>
  );
}

// ---------- zona de subida (file picker + drag/drop) ----------
function UploadZone({ file, onPick, onClear, accept, label, hint, icon = "Upload", compact }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const pick = (f) => { if (f) onPick({ name: f.name, size: humanSize(f.size), ext: extOf(f.name) }); };

  if (file) {
    const isVideo = ["mp4", "mov", "webm", "avi", "mkv"].includes(file.ext);
    const isSlides = ["pdf", "ppt", "pptx", "key"].includes(file.ext);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: "1px solid var(--kr-border)", borderRadius: 8, background: "var(--kr-bg-surface)" }}>
        <span style={{ width: 36, height: 36, borderRadius: 8, background: isVideo ? "var(--kr-status-info-bg)" : isSlides ? "var(--kr-status-warning-bg)" : "var(--kr-neutral-100)", color: isVideo ? "var(--kr-status-info-fg)" : isSlides ? "var(--kr-status-warning-fg)" : "var(--kr-fg-muted)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={isVideo ? "Play" : "FileText"} size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)", textTransform: "uppercase" }}>{file.ext} · {file.size}</div>
        </div>
        <button type="button" onClick={() => ref.current && ref.current.click()} style={linkBtn}>Reemplazar</button>
        <IconBtn icon="X" size={28} onClick={onClear} title="Quitar" />
        <input ref={ref} type="file" accept={accept} hidden onChange={(e) => pick(e.target.files[0])} />
      </div>
    );
  }
  return (
    <div
      onClick={() => ref.current && ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: compact ? "10px 14px" : "16px 16px",
        border: `1.5px dashed ${over ? "var(--kr-action)" : "var(--kr-border-strong)"}`,
        borderRadius: 8, background: over ? "var(--kr-action-soft-bg)" : "var(--kr-neutral-25)",
        cursor: "pointer", color: "var(--kr-fg-muted)",
      }}
    >
      <span style={{ width: compact ? 30 : 36, height: compact ? 30 : 36, borderRadius: 8, background: "var(--kr-bg-surface)", border: "1px solid var(--kr-border)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={icon} size={compact ? 15 : 17} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>{label} <span style={{ color: "var(--kr-fg-link)" }}>buscar archivo</span></div>
        {hint && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{hint}</div>}
      </div>
      <input ref={ref} type="file" accept={accept} hidden onChange={(e) => pick(e.target.files[0])} />
    </div>
  );
}

// ---------- fila de lección ----------
function LessonRow({ lesson, index, onChange, onRemove }) {
  const set = (patch) => onChange({ ...lesson, ...patch });
  const isVideo = lesson.type === "video";
  return (
    <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ width: 24, height: 24, borderRadius: 6, background: "var(--kr-neutral-150)", color: "var(--kr-fg-muted)", fontSize: 11, fontWeight: 700, fontFamily: "var(--kr-font-mono)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{index + 1}</span>
        <input
          value={lesson.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Título de la lección…"
          style={{ flex: 1, height: 34, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "0 10px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", outline: "none", background: "var(--kr-bg-surface)" }}
        />
        {/* toggle de tipo */}
        <div style={{ display: "inline-flex", background: "var(--kr-neutral-100)", borderRadius: 6, padding: 2, gap: 2, flexShrink: 0 }}>
          {[{ id: "video", label: "Vídeo", icon: "Play" }, { id: "presentation", label: "Pres.", icon: "FileText" }].map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => set({ type: o.id })}
              title={o.id === "video" ? "Vídeo" : "Presentación"}
              style={{
                border: "none", background: lesson.type === o.id ? "var(--kr-bg-surface)" : "transparent",
                boxShadow: lesson.type === o.id ? "var(--kr-shadow-xs)" : "none",
                color: lesson.type === o.id ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
                fontSize: 12, fontWeight: 600, padding: "5px 9px", borderRadius: 4, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
              }}
            >
              <Icon name={o.icon} size={12} />{o.label}
            </button>
          ))}
        </div>
        <input
          value={lesson.duration}
          onChange={(e) => set({ duration: e.target.value })}
          placeholder="08:30"
          style={{ width: 64, height: 34, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "0 8px", fontSize: 12, fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", outline: "none", background: "var(--kr-bg-surface)", textAlign: "center", flexShrink: 0 }}
        />
        <IconBtn icon="X" size={30} onClick={onRemove} title="Eliminar lección" />
      </div>
      <UploadZone
        compact
        file={lesson.file}
        onPick={(f) => set({ file: f })}
        onClear={() => set({ file: null })}
        accept={isVideo ? "video/*" : ".pdf,.ppt,.pptx,.key"}
        icon={isVideo ? "Play" : "FileText"}
        label={lesson.file ? "" : isVideo ? "Sube el vídeo o" : "Sube la presentación o"}
        hint={isVideo ? "MP4, MOV o WebM · máx. 2 GB" : "PDF, PPT o Keynote"}
      />
    </div>
  );
}

// ---------- editor de volumen ----------
function VolumeEditor({ volume, index, total, onChange, onRemove, onMove }) {
  const [open, setOpen] = useState(true);
  const set = (patch) => onChange({ ...volume, ...patch });
  const matRef = useRef(null);

  const addLesson = () => set({ lessons: [...volume.lessons, { id: uid("l"), title: "", type: "video", duration: "", file: null }] });
  const updLesson = (i, l) => set({ lessons: volume.lessons.map((x, j) => (j === i ? l : x)) });
  const rmLesson = (i) => set({ lessons: volume.lessons.filter((_, j) => j !== i) });
  const addMaterials = (files) => {
    const arr = Array.from(files || []).map((f) => ({ id: uid("m"), name: f.name, size: humanSize(f.size), ext: extOf(f.name) }));
    if (arr.length) set({ materials: [...volume.materials, ...arr] });
  };

  return (
    <Card padded={false} style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "var(--kr-neutral-50)", borderBottom: open ? "1px solid var(--kr-border-subtle)" : "none" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)", textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}>Vol {index + 1}</span>
        <input
          value={volume.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Título del volumen o módulo…"
          style={{ flex: 1, height: 34, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "0 10px", fontSize: 13, fontWeight: 600, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", outline: "none", background: "var(--kr-bg-surface)" }}
        />
        <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)", flexShrink: 0 }}>{volume.lessons.length} lec · {volume.materials.length} mat</span>
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          <IconBtn icon="ChevronDown" size={28} onClick={() => onMove(1)} title="Bajar" style={{ opacity: index === total - 1 ? 0.35 : 1 }} />
          <IconBtn icon="ChevronDown" size={28} onClick={() => onMove(-1)} title="Subir" style={{ transform: "rotate(180deg)", opacity: index === 0 ? 0.35 : 1 }} />
          <IconBtn icon="X" size={28} onClick={onRemove} title="Eliminar volumen" />
          <IconBtn icon={open ? "ChevronDown" : "ChevronRight"} size={28} onClick={() => setOpen((o) => !o)} title={open ? "Contraer" : "Expandir"} />
        </div>
      </div>

      {open && (
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* lecciones */}
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 10 }}>Lecciones</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {volume.lessons.map((l, i) => (
                <LessonRow key={l.id} lesson={l} index={i} onChange={(nl) => updLesson(i, nl)} onRemove={() => rmLesson(i)} />
              ))}
              {volume.lessons.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)", padding: "10px 12px", border: "1px dashed var(--kr-border)", borderRadius: 8, textAlign: "center" }}>
                  Aún no hay lecciones en este volumen.
                </div>
              )}
            </div>
            <button type="button" onClick={addLesson} style={addRowBtn}><Icon name="Plus" size={14} />Añadir lección</button>
          </div>

          {/* materiales */}
          <div style={{ borderTop: "1px solid var(--kr-border-subtle)", paddingTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Materiales del volumen</div>
              <button type="button" onClick={() => matRef.current && matRef.current.click()} style={linkBtn}><Icon name="Upload" size={12} style={{ marginRight: 4 }} />Subir archivos</button>
              <input ref={matRef} type="file" multiple hidden onChange={(e) => { addMaterials(e.target.files); e.target.value = ""; }} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.png,.jpg" />
            </div>
            {volume.materials.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {volume.materials.map((m, i) => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid var(--kr-border-subtle)", borderRadius: 8 }}>
                    <span style={{ width: 30, height: 36, borderRadius: 4, background: "var(--kr-status-danger-bg)", color: "var(--kr-danger-700)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, textTransform: "uppercase", flexShrink: 0 }}>{m.ext}</span>
                    <span style={{ flex: 1, fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.name}</span>
                    <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{m.size}</span>
                    <IconBtn icon="X" size={26} onClick={() => set({ materials: volume.materials.filter((_, j) => j !== i) })} title="Quitar" />
                  </div>
                ))}
              </div>
            ) : (
              <div onClick={() => matRef.current && matRef.current.click()} style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: 12, border: "1.5px dashed var(--kr-border-strong)", borderRadius: 8, textAlign: "center", cursor: "pointer", background: "var(--kr-neutral-25)" }}>
                <Icon name="Paperclip" size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />Guías, plantillas, PDF, hojas de cálculo… <span style={{ color: "var(--kr-fg-link)", fontWeight: 600 }}>buscar</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// =====================================================================
//  MAIN — Crear formación
// =====================================================================
function FormacionCrear() {
  document.title = "Crear formación | Kronaly";
  const navigate = useNavigate();
  const onBack = () => navigate("/formacion");

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [instructor, setInstructor] = useState("");
  const [instructorRole, setInstructorRole] = useState("");
  const [type, setType] = useState("video");
  const [mandatory, setMandatory] = useState(false);
  const [deadline, setDeadline] = useState("");
  const [accent, setAccent] = useState("brand");
  const [cover, setCover] = useState(null);
  const [learn, setLearn] = useState([""]);
  const [requirements, setRequirements] = useState([""]);
  const [volumes, setVolumes] = useState([
    { id: uid("v"), title: "", lessons: [{ id: uid("l"), title: "", type: "video", duration: "", file: null }], materials: [] },
  ]);

  const cat = CONTENT_CATS.find((c) => c.id === categoryId);
  const totalLessons = volumes.reduce((a, v) => a + v.lessons.length, 0);
  const totalMaterials = volumes.reduce((a, v) => a + v.materials.length, 0);
  const totalMin = Math.round(volumes.reduce((a, v) => a + v.lessons.reduce((b, l) => b + durToMin(l.duration), 0), 0));
  const canSubmit = title.trim() && categoryId && volumes.some((v) => v.lessons.length > 0);

  // objeto curso para la vista previa (CourseThumb)
  const previewCourse = useMemo(() => ({
    id: "preview",
    title: title || "Título de la formación",
    category: cat ? cat.label : "Categoría",
    type, durationMin: totalMin || 0, accent, enrolled: false,
    volumes: volumes.map((v) => ({ ...v, lessons: v.lessons.map((l) => ({ ...l, completed: false })) })),
  }), [title, cat, type, totalMin, accent, volumes]);

  // helpers de listas (qué aprenderás / requisitos)
  const setListItem = (list, setList) => (i, val) => setList(list.map((x, j) => (j === i ? val : x)));
  const addListItem = (list, setList) => () => setList([...list, ""]);
  const rmListItem = (list, setList) => (i) => setList(list.filter((_, j) => j !== i));

  // helpers de volúmenes
  const addVolume = () => setVolumes([...volumes, { id: uid("v"), title: "", lessons: [], materials: [] }]);
  const updVolume = (i, v) => setVolumes(volumes.map((x, j) => (j === i ? v : x)));
  const rmVolume = (i) => setVolumes(volumes.filter((_, j) => j !== i));
  const moveVolume = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= volumes.length) return;
    const copy = [...volumes];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setVolumes(copy);
  };

  return (
    <div className="kr-page" style={{ gap: 18 }}>
      <button
        type="button"
        onClick={onBack}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}
      >
        <Icon name="ArrowLeft" size={14} />Volver a Formación
      </button>

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Aprendizaje · Nueva formación</div>
          <h1 className="kr-display" style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Crear formación</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>Define el curso, organiza sus volúmenes y sube los vídeos, presentaciones y materiales.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" icon="FileText" disabled={!title.trim()}>Guardar borrador</Button>
          <Button variant="primary" icon="Check" disabled={!canSubmit} onClick={onBack}>Publicar formación</Button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }} data-responsive-grid="2">
        {/* ---------------- FORM ---------------- */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* detalles */}
          <Card>
            <SecTitle eyebrow="Paso 1" title="Detalles del curso" />
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
              <Field label="Título de la formación" required>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Protección de datos y RGPD" />
              </Field>
              <Field label="Resumen corto" help="Una frase que aparece en la tarjeta del catálogo.">
                <Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Cómo tratar datos personales en tu día a día." />
              </Field>
              <Field label="Descripción">
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Describe el objetivo, el alcance y a quién va dirigido…" style={taStyle} />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field label="Categoría" required>
                  <FmSelect value={categoryId} onChange={setCategoryId} placeholder="Selecciona…" options={CONTENT_CATS.map((c) => ({ value: c.id, label: c.label }))} />
                </Field>
                <Field label="Formato principal">
                  <div style={{ display: "inline-flex", background: "var(--kr-neutral-100)", borderRadius: 6, padding: 2, gap: 2, width: "100%" }}>
                    {[{ id: "video", label: "Vídeo", icon: "Play" }, { id: "presentation", label: "Presentación", icon: "FileText" }].map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setType(o.id)}
                        style={{ flex: 1, border: "none", background: type === o.id ? "var(--kr-bg-surface)" : "transparent", boxShadow: type === o.id ? "var(--kr-shadow-xs)" : "none", color: type === o.id ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, padding: "7px 9px", borderRadius: 4, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                      >
                        <Icon name={o.icon} size={12} />{o.label}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Formador / instructor">
                  <Input value={instructor} onChange={(e) => setInstructor(e.target.value)} placeholder="Nombre del formador" />
                </Field>
                <Field label="Rol del formador">
                  <Input value={instructorRole} onChange={(e) => setInstructorRole(e.target.value)} placeholder="DPO · Cabrera Legal" />
                </Field>
              </div>

              {/* obligatorio + fecha límite */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "end" }}>
                <Field label="Obligatoriedad">
                  <button
                    type="button"
                    onClick={() => setMandatory((m) => !m)}
                    style={{
                      height: 36, display: "inline-flex", alignItems: "center", gap: 8, padding: "0 12px",
                      border: `1px solid ${mandatory ? "var(--kr-warning-500)" : "var(--kr-border)"}`,
                      background: mandatory ? "var(--kr-status-warning-bg)" : "var(--kr-bg-surface)",
                      color: mandatory ? "var(--kr-warning-700)" : "var(--kr-fg-muted)",
                      borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", width: "100%", justifyContent: "flex-start",
                    }}
                  >
                    <span style={{ width: 34, height: 18, borderRadius: 999, background: mandatory ? "var(--kr-warning-500)" : "var(--kr-neutral-300)", position: "relative", transition: "background .15s", flexShrink: 0 }}>
                      <span style={{ position: "absolute", top: 2, left: mandatory ? 18 : 2, width: 14, height: 14, borderRadius: 999, background: "#fff", transition: "left .15s" }} />
                    </span>
                    Curso obligatorio
                  </button>
                </Field>
                <Field label="Fecha límite" help={mandatory ? "Recomendado para obligatorios" : "Opcional"}>
                  <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                </Field>
              </div>

              {/* portada + acento */}
              <Field label="Portada y color">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "start" }}>
                  <UploadZone compact file={cover} onPick={setCover} onClear={() => setCover(null)} accept="image/*" icon="Upload" label="Imagen de portada o" hint="JPG o PNG · 16:10" />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {ACCENT_KEYS.map((k) => {
                      const a = FORMACION_ACCENTS[k];
                      const sel = accent === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setAccent(k)}
                          title={k}
                          style={{
                            width: 30, height: 30, borderRadius: 8, cursor: "pointer",
                            background: `linear-gradient(135deg, ${a.from}, ${a.to})`,
                            border: sel ? "2px solid var(--kr-fg-strong)" : "2px solid transparent",
                            boxShadow: sel ? "0 0 0 2px var(--kr-bg-surface) inset" : "none",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </Field>
            </div>
          </Card>

          {/* qué aprenderán + requisitos */}
          <Card>
            <SecTitle eyebrow="Paso 2" title="Qué aprenderán y requisitos" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
              <BulletList label="Qué aprenderás" icon="Check" items={learn} onChange={setListItem(learn, setLearn)} onAdd={addListItem(learn, setLearn)} onRemove={rmListItem(learn, setLearn)} placeholder="Un objetivo de aprendizaje…" />
              <BulletList label="Requisitos previos" icon="List" items={requirements} onChange={setListItem(requirements, setRequirements)} onAdd={addListItem(requirements, setRequirements)} onRemove={rmListItem(requirements, setRequirements)} placeholder="Un requisito o recomendación…" />
            </div>
          </Card>

          {/* contenido / volúmenes */}
          <Card padded={false}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <SecTitle eyebrow="Paso 3" title="Contenido del curso" />
              <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{volumes.length} vol · {totalLessons} lec · {totalMaterials} mat</span>
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, background: "var(--kr-neutral-50)" }}>
              {volumes.map((v, i) => (
                <VolumeEditor key={v.id} volume={v} index={i} total={volumes.length} onChange={(nv) => updVolume(i, nv)} onRemove={() => rmVolume(i)} onMove={(dir) => moveVolume(i, dir)} />
              ))}
              <button type="button" onClick={addVolume} style={{ ...addRowBtn, height: 40, justifyContent: "center", alignSelf: "stretch", borderColor: "var(--kr-action)", color: "var(--kr-action-soft-fg)", background: "var(--kr-action-soft-bg)", borderStyle: "solid" }}>
                <Icon name="Plus" size={15} />Añadir volumen / módulo
              </button>
            </div>
          </Card>
        </div>

        {/* ---------------- VISTA PREVIA ---------------- */}
        <div style={{ position: "sticky", top: 0, display: "flex", flexDirection: "column", gap: 16 }}>
          <Card padded={false} style={{ overflow: "hidden" }}>
            <CourseThumb course={previewCourse} />
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Vista previa</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {mandatory && <Badge tone="warning" dot>Obligatorio</Badge>}
                {cat && <Badge tone="neutral" variant="soft">{cat.label}</Badge>}
                <Badge tone="info" variant="soft">{type === "video" ? "Vídeo" : "Presentación"}</Badge>
              </div>
              <div style={{ fontFamily: "var(--kr-font-display)", fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em", lineHeight: 1.25 }}>
                {title || "Título de la formación"}
              </div>
              {summary && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", lineHeight: 1.45 }}>{summary}</div>}
              {instructor && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                  <Avatar name={instructor} size={22} />
                  <div style={{ fontSize: 11, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                    {instructor}
                    {instructorRole && <div style={{ color: "var(--kr-fg-muted)", fontWeight: 400 }}>{instructorRole}</div>}
                  </div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)", borderTop: "1px solid var(--kr-border-subtle)", paddingTop: 10, marginTop: 2 }}>
                <span>{volumes.length} vol</span><span>{totalLessons} lec</span><span>{fmtMinutes(totalMin)}</span>
              </div>
            </div>
          </Card>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", lineHeight: 1.5, padding: "0 4px" }}>
            <Icon name="HelpCircle" size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
            Los alumnos verán esta tarjeta en el catálogo. Los volúmenes se desbloquean en orden y la duración se calcula a partir de las lecciones.
          </div>
        </div>
      </div>
    </div>
  );
}

function SecTitle({ eyebrow, title }) {
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{eyebrow}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: 4 }}>{title}</div>
    </div>
  );
}

function BulletList({ label, icon, items, onChange, onAdd, onRemove, placeholder }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--kr-action-soft-bg)", color: "var(--kr-action-soft-fg)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={icon} size={12} />
            </span>
            <input
              value={it}
              onChange={(e) => onChange(i, e.target.value)}
              placeholder={placeholder}
              style={{ flex: 1, height: 34, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "0 10px", fontSize: 13, fontFamily: "var(--kr-font-sans)", color: "var(--kr-fg-strong)", outline: "none", background: "var(--kr-bg-surface)" }}
            />
            <IconBtn icon="X" size={28} onClick={() => onRemove(i)} title="Quitar" />
          </div>
        ))}
      </div>
      <button type="button" onClick={onAdd} style={addRowBtn}><Icon name="Plus" size={14} />Añadir</button>
    </div>
  );
}

export default FormacionCrear;
